import React, { useCallback, useEffect, useState } from "react";
import { api, ApiClientError } from "../api";
import type { Id, Task } from "../types";
import { useToast } from "../context/ToastContext";
import { useAuth } from "../context/AuthContext";
import { useDrawer } from "../context/DrawerContext";
import { formatDateTime } from "../lib/format";

type Scope = "mine" | "today" | "overdue" | "team";

const SCOPES: { key: Scope; label: string }[] = [
  { key: "mine", label: "Mis tareas" },
  { key: "today", label: "Vencen hoy" },
  { key: "overdue", label: "Vencidas" },
  { key: "team", label: "Equipo" },
];

export default function Tasks() {
  const { show } = useToast();
  const { isAdminLike } = useAuth();
  const { open } = useDrawer();
  const [scope, setScope] = useState<Scope>("mine");
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getTasks(scope);
      setTasks(res);
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudieron cargar las tareas.", "error");
    } finally {
      setLoading(false);
    }
  }, [scope, show]);

  useEffect(() => {
    load();
  }, [load]);

  async function complete(id: Id) {
    try {
      await api.completeTask(id);
      show("Tarea completada.", "success");
      load();
    } catch (e) {
      show("No se pudo completar la tarea.", "error");
    }
  }

  async function openRelated(task: Task) {
    if (!task.opportunity_id) return;
    try {
      const opp = await api.getOpportunity(task.opportunity_id);
      if (opp.contact_id) open(opp.contact_id);
    } catch {
      show("No se pudo abrir la ficha relacionada.", "error");
    }
  }

  return (
    <div className="tasks-page">
      <h1>Centro de tareas</h1>
      <div className="pipeline-tabs">
        {SCOPES.filter((s) => s.key !== "team" || isAdminLike).map((s) => (
          <button key={s.key} className={`tab ${scope === s.key ? "active" : ""}`} onClick={() => setScope(s.key)}>
            {s.label}
          </button>
        ))}
      </div>

      {loading && <div className="loading-strip">Cargando tareas...</div>}

      <table className="task-table">
        <thead>
          <tr>
            <th></th>
            <th>Título</th>
            <th>Relacionado</th>
            <th>Vencimiento</th>
            <th>Prioridad</th>
            <th>Estado</th>
          </tr>
        </thead>
        <tbody>
          {tasks.map((t) => (
            <tr key={t.id} className={t.status === "done" ? "task-done" : ""}>
              <td>
                <input type="checkbox" checked={t.status === "done"} disabled={t.status === "done"} onChange={() => complete(t.id)} />
              </td>
              <td>{t.title}</td>
              <td>
                {t.opportunity_id ? (
                  <button className="link-btn" onClick={() => openRelated(t)}>
                    {t.contact_name || "Ver ficha"}
                  </button>
                ) : (
                  "-"
                )}
              </td>
              <td className={t.status !== "done" && new Date(t.due_at) < new Date() ? "overdue" : ""}>{formatDateTime(t.due_at)}</td>
              <td>{t.priority || "normal"}</td>
              <td>{t.status}</td>
            </tr>
          ))}
          {!loading && tasks.length === 0 && (
            <tr>
              <td colSpan={6} className="meta">
                No hay tareas en esta vista.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
